import { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useSpamProtection } from "@/hooks/useSpamProtection";
import SpamDebugBadge from "@/components/SpamDebugBadge";
import { gtagEvent } from "@/lib/gtag";
import { getAttribution } from "@/lib/attribution";


interface ContactFormProps {
  showService?: boolean;
  /** Three-field variant: Name, Phone, Message only (same submission path). */
  compact?: boolean;
  /** Make Service Needed required (only meaningful when showService=true). */
  requireService?: boolean;
  /** Show & require service-address field. */
  showAddress?: boolean;
  /** Show & require preferred-contact-time field. */
  showContactTime?: boolean;
  source?: string;
}

const ContactForm = ({
  showService = true,
  requireService = false,
  showAddress = false,
  showContactTime = false,
  compact = false,
  source = "contact_form",
}: ContactFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { honeypotProps, mountTime, checkSpam, lastResult, debug } = useSpamProtection();


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return; // block double-submits while a request is in flight

    const form = e.currentTarget;
    const data = new FormData(form);

    // Browser-side check is advisory only: the submission is ALWAYS sent and
    // the server's screenLead decides. Never drop a lead in the browser.
    const spam = checkSpam({
      name: String(data.get("name") || ""),
      email: String(data.get("email") || ""),
      company: String(data.get("company") || ""),
      message: String(data.get("message") || ""),
      source,
    });


    setLoading(true);

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      const res = await fetch(`${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/submit-lead`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']}`,
          apikey: import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'],
        },
        body: JSON.stringify({
          name: data.get("name"),
          phone: data.get("phone"),
          // Email is optional in the compact variant; the backend requires one,
          // so a routing placeholder is sent only when the field is left blank.
          email: data.get("email") || "no-email-provided@hadleyenergysolutions.com",
          // Omit company when empty (edge function stores null) — never an empty string.
          company: data.get("company") || undefined,
          service: data.get("service") || "",
          contactMethod: data.get("contact-method") || "",
          address: data.get("address") || "",
          contactTime: data.get("contact-time") || "",
          message: data.get("message"),
          source,
          honeypot: String(data.get("hp_x7") || ""),
          elapsedMs: Date.now() - mountTime.current,
          clientFlags: { isSpam: spam.isSpam, reason: spam.reason ?? null },
          attribution: getAttribution(),
        }),
        signal: controller.signal,
      });

      clearTimeout(timeout);
      if (!res.ok) {
        let payload: { error?: string; message?: string } = {};
        try { payload = await res.json(); } catch { /* ignore */ }
        if (payload.error === "rate_limited") {
          toast({
            title: "Slow down a moment",
            description: payload.message || "Please try again shortly, or call us at (502) 921-3777.",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        throw new Error("Failed to submit");
      }



      // GA4 conversion events — fire both `generate_lead` (GA4 recommended) and `form_submit`,
      // tagged with first-touch attribution so source/medium/campaign show in GA4 reports.
      const attr = getAttribution();
      const attrParams = {
        utm_source: attr.utm_source || "(direct)",
        utm_medium: attr.utm_medium || "(none)",
        utm_campaign: attr.utm_campaign || "(none)",
        utm_term: attr.utm_term || "",
        utm_content: attr.utm_content || "",
        gclid: attr.gclid || "",
        fbclid: attr.fbclid || "",
        landing_page: attr.landing_page || "",
        referrer: attr.referrer || "",
      };
      gtagEvent("generate_lead", {
        event_category: "form",
        event_label: source,
        form_source: source,
        currency: "USD",
        value: 1,
        ...attrParams,
      });
      gtagEvent("form_submit", {
        event_category: "form",
        event_label: source,
        form_source: source,
        ...attrParams,
      });

      setSubmitted(true);
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please call us at (502) 921-3777 or try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
          <Send className="w-8 h-8 text-primary-foreground" />
        </div>
        <h3 className="font-heading text-2xl font-bold mb-2">Thank You!</h3>
        <p className="text-muted-foreground font-body mb-4">We've received your message and will get back to you within one business day. For immediate assistance, call (502) 921-3777.</p>
        <p className="text-sm text-muted-foreground font-body mb-3">If you've worked with us before, we'd appreciate a Google review!</p>
        <a href="https://share.google/wAzK0RXCbRzvte5d0" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-primary font-heading font-bold text-sm hover:underline">
          Leave a Review on Google ⭐
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot — invisible to humans, irresistible to bots. Do not remove. */}
      <input {...honeypotProps} />
      <SpamDebugBadge result={lastResult} enabled={debug} />
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-heading font-semibold mb-1">Name *</label>
          <input
            id="name"
            name="name"
            type="text"
            required
            maxLength={100}
            placeholder="Jane Smith"
            autoComplete="name"
            onInvalid={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("Please enter your name so we know who to call back.")}
            onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("")}
            className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-heading font-semibold mb-1">Phone *</label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            maxLength={20}
            minLength={7}
            pattern="[\d\s().+\-]{7,}"
            placeholder="(502) 555-0123"
            autoComplete="tel"
            onInvalid={(e) => {
              const el = e.currentTarget as HTMLInputElement;
              el.setCustomValidity(el.value.trim() === "" ? "Please enter a phone number we can reach you at." : "Please enter a valid phone number (digits only).");
            }}
            onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("")}
            className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>
      {compact ? (
        <>
          {/* Compact variant: optional Email as the 3rd field (Name / Phone / Email / Message). */}
          <div>
            <label htmlFor="email" className="block text-sm font-heading font-semibold mb-1">
              Email
              <span className="ml-2 font-body font-normal text-xs text-muted-foreground">Optional — helpful for quotes and follow-ups.</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              maxLength={255}
              placeholder="you@example.com"
              autoComplete="email"
              onInvalid={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("That doesn't look like a valid email — please double-check.")}
              onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("")}
              className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50"
            />
          </div>
          {/* Company stays in the DOM but hidden; empty value is omitted on submit. */}
          <input id="company" name="company" type="hidden" maxLength={100} />
        </>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-sm font-heading font-semibold mb-1">Email *</label>
            <input
              id="email"
              name="email"
              type="email"
              required
              maxLength={255}
              placeholder="you@example.com"
              autoComplete="email"
              onInvalid={(e) => {
                const el = e.currentTarget as HTMLInputElement;
                el.setCustomValidity(el.value.trim() === "" ? "Please enter your email address." : "That doesn't look like a valid email — please double-check.");
              }}
              onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("")}
              className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label htmlFor="company" className="block text-sm font-heading font-semibold mb-1">Company Name</label>
            <input id="company" name="company" type="text" maxLength={100} placeholder="Optional — e.g., Louisville Metro Schools" autoComplete="organization" className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50" />
          </div>
        </div>
      )}
      {showService && !compact && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="service" className="block text-sm font-heading font-semibold mb-1">Service Needed{requireService ? " *" : ""}</label>
            <select
              id="service"
              name="service"
              required={requireService}
              onInvalid={(e) => (e.currentTarget as HTMLSelectElement).setCustomValidity("Please choose the service you need.")}
              onInput={(e) => (e.currentTarget as HTMLSelectElement).setCustomValidity("")}
              className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50"
            >
              <option value="">Select a service...</option>
              <option>Electrical Service & Repair</option>
              <option>Electrical Contracting (New Construction)</option>
              <option>Preventative Maintenance</option>
              <option>Generator Installation</option>
              <option>HVAC Installation</option>
              <option>HVAC Repair & Service</option>
              <option>Building Automation / Controls</option>
              <option>Lighting Design</option>
              <option>Lighting Retrofit / LED Upgrade</option>
              <option>Energy Audit</option>
              <option>Energy Bill Analysis</option>
              <option>Stadium & Sports Lighting</option>
              <option>Emergency Service</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label htmlFor="contact-method" className="block text-sm font-heading font-semibold mb-1">Preferred Contact Method</label>
            <select id="contact-method" name="contact-method" className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50">
              <option value="">Select...</option>
              <option>Phone Call</option>
              <option>Email</option>
              <option>Text Message</option>
            </select>
          </div>
        </div>
      )}
      {(showAddress || showContactTime) && (
        <div className="grid sm:grid-cols-2 gap-4">
          {showAddress && (
            <div className={showContactTime ? "" : "sm:col-span-2"}>
              <label htmlFor="address" className="block text-sm font-heading font-semibold mb-1">Service Address *</label>
              <input
                id="address"
                name="address"
                type="text"
                required
                maxLength={200}
                placeholder="123 Main St, Louisville, KY 40202"
                autoComplete="street-address"
                onInvalid={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("Please share the street address where the work is needed.")}
                onInput={(e) => (e.currentTarget as HTMLInputElement).setCustomValidity("")}
                className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50"
              />
            </div>
          )}
          {showContactTime && (
            <div className={showAddress ? "" : "sm:col-span-2"}>
              <label htmlFor="contact-time" className="block text-sm font-heading font-semibold mb-1">Preferred Contact Time *</label>
              <select
                id="contact-time"
                name="contact-time"
                required
                onInvalid={(e) => (e.currentTarget as HTMLSelectElement).setCustomValidity("Please pick the time of day that works best for you.")}
                onInput={(e) => (e.currentTarget as HTMLSelectElement).setCustomValidity("")}
                className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50"
              >
                <option value="">Select a time...</option>
                <option>Morning (8am – 12pm)</option>
                <option>Afternoon (12pm – 5pm)</option>
                <option>Evening (5pm – 8pm)</option>
                <option>Anytime during business hours</option>
                <option>ASAP — it's urgent</option>
              </select>
            </div>
          )}
        </div>
      )}
      <div>
        <label htmlFor="message" className="block text-sm font-heading font-semibold mb-1">
          Message *
          <span className="ml-2 font-body font-normal text-xs text-muted-foreground">
            Tell us your city or neighborhood — it helps us route faster.
          </span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          maxLength={1000}
          rows={compact ? 4 : 5}
          placeholder={'e.g., "HVAC unit in St. Matthews stopped cooling — need a quote." or "Need an electrical panel upgrade for our shop in J-Town."'}
          onInvalid={(e) => (e.currentTarget as HTMLTextAreaElement).setCustomValidity("Please tell us a bit about what you need so we can help faster.")}
          onInput={(e) => (e.currentTarget as HTMLTextAreaElement).setCustomValidity("")}
          className="w-full px-4 py-3 border rounded-lg bg-background font-body focus:outline-hidden focus:ring-2 focus:ring-primary/50 resize-none"
        />
      </div>
      
      <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-4 rounded-lg font-heading font-bold text-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        {loading ? "Sending..." : "Send Message"}
      </button>
      <p className="text-xs text-muted-foreground text-center font-body">By submitting this form you agree to our <a href="/privacy-policy" className="text-primary underline">Privacy Policy</a>.</p>
    </form>
  );
};

export default ContactForm;